import isNull from "lodash/isNull";
import * as React from "react";
import { DragSource } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { observer } from "mobx-react";

import {
    DND_SECTION_HANDLE,
    DND_SECTION_HANDLE_LEFT,
    DND_SECTION_HANDLE_RIGHT,
    onSectionInPointDrag,
    onSectionOutPointDrag
} from "pages/editor/lib/timeline";

import { undoManager } from "pages/editor/state/store/undomanager";

import { SectionHandleWrapper, InnerHandleGrabber } from "./section.styles";

const spec = {
    beginDrag: (props) => {
        props.ui.onSectionHandleDragStart(props.section.id);
        return {
            id: props.section.id,
            side: props.side
        };
    },
    endDrag: ({ ui }) => ui.onSectionHandleDragEnd()
};

function collect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
        monitor
    };
}

class SectionHandle extends React.Component {
    constructor(props) {
        super(props);
        this.onDragging = this.onDragging.bind(this);
    }

    componentDidMount() {
        const { connectDragPreview } = this.props;
        if (connectDragPreview) {
            // Use empty image as a drag preview
            connectDragPreview(getEmptyImage(), {
                captureDraggingState: true
            });
        }
    }

    /**
     * @summary The drag operation for a section handle is handled here - because we want to update an inPoint/outPoint
     * without necessarily hovering over a drop target, we use a RAF loop while the drag source is being dragged
     * to collect the drag operation values which are passed to the relevant handlers
     */
    componentDidUpdate() {
        const { isDragging, ui } = this.props;
        if (isDragging && !this.requestAnimationFrameId) {
            undoManager.startGroup(() => {
                this.requestAnimationFrameId = window.requestAnimationFrame(
                    this.onDragging
                );
            });
        } else if (!isDragging && this.requestAnimationFrameId) {
            ui.composition.updateConnectedSections();
            window.cancelAnimationFrame(this.requestAnimationFrameId);
            undoManager.stopGroup();
            delete this.requestAnimationFrameId;
            delete this.prevOffset;
        }
    }

    onDragging() {
        const { monitor, ui, section } = this.props;
        const { x } = section;
        const offset = monitor.getClientOffset();

        if (!isNull(offset)) {
            if (!this.prevOffset) {
                this.prevOffset = offset.x;
            }
            const offsetDelta = offset.x - this.prevOffset;
            this.prevOffset = offset.x;

            const { side } = monitor.getItem();

            if (side === DND_SECTION_HANDLE_LEFT) {
                let time;
                if (section.isChildOfBaseLayer) {
                    time = ui.timelinePxToTime(x + offsetDelta);
                } else {
                    time = ui.getMousePositionAsTime(offset.x);
                    if (ui.snappingEnabled) {
                        const snapOperation = ui.snapOnTimelineByTime([time]);
                        const validSnapOperation =
                            snapOperation &&
                            section.isWithinInPointBoundaries(snapOperation.to);

                        if (validSnapOperation) {
                            ui.setSnapGuidePositionByTime(snapOperation.to);
                            time = snapOperation.to;
                        } else if (ui.snapGuidePosition !== null) {
                            ui.setSnapGuidePosition(null);
                        }
                    }
                }

                onSectionInPointDrag(section, time);
            }

            if (side === DND_SECTION_HANDLE_RIGHT) {
                let time = ui.getMousePositionAsTime(offset.x);

                if (!section.isChildOfBaseLayer && ui.snappingEnabled) {
                    const snapOperation = ui.snapOnTimelineByTime([time]);
                    const validSnapOperation =
                        snapOperation &&
                        section.isWithinOutPointBoundaries(snapOperation.to);

                    if (validSnapOperation) {
                        ui.setSnapGuidePositionByTime(snapOperation.to);
                        time = snapOperation.to;
                    } else if (ui.snapGuidePosition !== null) {
                        ui.setSnapGuidePosition(null);
                    }
                }

                onSectionOutPointDrag(section, time);
            }

            this.requestAnimationFrameId = window.requestAnimationFrame(
                this.onDragging
            );
        } else {
            console.log(
                `Offset was null, RAF fired after section handle stopped dragging`
            );
        }
    }

    render() {
        const { connectDragSource, section, side } = this.props;
        return connectDragSource(
            <div>
                <SectionHandleWrapper
                    data-testid={`section-handle-${section.id}-${side}`}
                    isActive={section.selected}
                    type={section.type}
                    side={side}
                >
                    <InnerHandleGrabber grabber={DND_SECTION_HANDLE_LEFT} />
                    <InnerHandleGrabber grabber={DND_SECTION_HANDLE_RIGHT} />
                </SectionHandleWrapper>
            </div>
        );
    }
}

export default DragSource(
    DND_SECTION_HANDLE,
    spec,
    collect
)(observer(SectionHandle));
